"use client";

import React, { useRef, useCallback } from "react";
import {
	ApolloClient,
	ApolloNextAppProvider,
	InMemoryCache,
} from "@apollo/client-integration-nextjs";
import { useApolloClient } from "@apollo/client/react";
import { useIsomorphicLayoutEffect } from "usehooks-ts";
import { useValueListener } from "@jasperlabs/hooks";
import { useActiveTenant } from "@/tenants/TenantProvider";

import { useSession } from "../contexts/session/session";
import { useEnvironmentVariables } from "../contexts/environmentVariables";
import { useLogger } from "../contexts/logger";

import { useRunOnNextTick } from "../components/utilities/useTimeout";

import { createClient, createInMemoryCache } from "./client";
import {
	clearAllApolloClientStores,
	releaseApolloClientSingletonFor,
	trackApolloClient,
} from "./client/browserApolloClients";

export { clearAllApolloClientStores };

function useGetLatestSessionToken() {
	const { sessionToken } = useSession();
	const ref = useRef(sessionToken);

	useIsomorphicLayoutEffect(() => {
		ref.current = sessionToken;
	}, [sessionToken]);

	return useCallback(() => ref.current, []);
}

/**
 * Resets the store when the session token changes, so one session's records
 * never reach the next.
 *
 * It reads the client from context because `ApolloNextAppProvider` builds and
 * owns the client, so this is the handle that still finds it. Kept as a reset on
 * the same client, not a new client per token: a new client would remount the
 * whole tree on every investing-entity switch, which changes the token, and
 * that is the full-page flash APP-27270 removed.
 */
function ResetStoreOnSessionChange() {
	const client = useApolloClient();
	const { sessionToken } = useSession();
	const runOnNextTick = useRunOnNextTick();

	useValueListener({
		value: sessionToken,
		onChange({ previous }: { previous: unknown }) {
			// If there was a token then reset the store regardless of the changed value
			if (previous) {
				// Apollo Client 4's resetStore rejects when any query it refetches
				// fails. Each failure already reaches the error link and its own
				// query's result, so the rejection is caught rather than left to
				// surface as an unhandled one.
				runOnNextTick(() => {
					client.resetStore().catch(() => {});
				});
			}
		},
	});

	return null;
}

const GraphQLProvider = ({ children }: { children: React.ReactNode }) => {
	const tenant = useActiveTenant();

	const { graphQLApiUri, runtimeEnvironment } = useEnvironmentVariables();
	const { expireSession } = useSession();
	const getToken = useGetLatestSessionToken();
	const { captureException } = useLogger();

	// In local-dev, jasper-api takes the tenant from the X-Tenant-Id header (the
	// Origin->tenant path is unavailable locally), and the localhost mock tenant's
	// id is not the local-dev tenant. Send the local-dev tenant id instead. No
	// effect in any deployed environment (NEXT_PUBLIC_LOCAL_DEV_TENANT_ID unset).
	const tenantId =
		runtimeEnvironment === "local"
			? (process.env.NEXT_PUBLIC_LOCAL_DEV_TENANT_ID ?? tenant?.tenantId)
			: tenant?.tenantId;

	// `ApolloNextAppProvider` builds the client itself and owns its lifetime. It
	// is keyed on the tenant and host below, so a change to either builds a new
	// client rather than reusing one bound to the wrong tenant. In the browser the
	// remount alone is not enough, because the provider reuses its window
	// singleton; `releaseApolloClientSingletonFor` drops it when the key changes.
	// The cache is built with the client, never held by this component, so a new
	// client never starts from the previous tenant's records.
	const makeClient = (): ApolloClient => {
		// `createClient` is plain JavaScript, so TypeScript infers Apollo's own
		// `ApolloClient` for it whatever constructor it is given. The integration's
		// is a branded subclass; this states what was actually built.
		const client = createClient({
			cache: createInMemoryCache(InMemoryCache),
			uri: graphQLApiUri,
			getToken,
			onInvalidToken: expireSession,
			// Report routed GraphQL/network errors (incl. generic 429 network
			// errors) as exceptions so they surface in Sentry's Issues stream.
			// Mirrors admin-app's apollo error link.
			onError: captureException,
			tenantId,
			ClientImpl: ApolloClient,
		}) as ApolloClient;

		trackApolloClient(client);

		return client;
	};

	const clientKey = `${tenantId ?? ""}::${graphQLApiUri ?? ""}`;
	releaseApolloClientSingletonFor(clientKey);

	return (
		<ApolloNextAppProvider key={clientKey} makeClient={makeClient}>
			<ResetStoreOnSessionChange />
			{children}
		</ApolloNextAppProvider>
	);
};

export default GraphQLProvider;
